import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';

const RED = '#8B1A1A';
const UPPER_WORDS = new Set(['NW','NE','SW','SE','PO']);
function titleCase(s){
  if(!s)return'';
  return s.replace(/\S+/g,(w,i,src)=>{
    const u=w.toUpperCase();
    if(UPPER_WORDS.has(u))return u;
    if(u==='BOX'&&i>0&&src.slice(Math.max(0,i-3),i).trim().toUpperCase()==='PO')return'Box';
    return w.charAt(0).toUpperCase()+w.slice(1).toLowerCase();
  });
}

const s = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: '#333' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 30 },
  logoBlock: { flexDirection: 'column', maxWidth: 220 },
  logo: { width: 180, objectFit: 'contain', marginBottom: 8 },
  bizName: { fontSize: 14, fontWeight: 'bold', color: RED },
  bizDetail: { fontSize: 9, color: '#666', marginTop: 2 },
  invoiceBlock: { textAlign: 'right' },
  invoiceTitle: { fontSize: 24, fontWeight: 'bold', color: RED, marginBottom: 6 },
  invoiceNum: { fontSize: 12, fontWeight: 'bold', marginBottom: 2 },
  invoiceDate: { fontSize: 10, color: '#666' },
  billTo: { marginBottom: 24, padding: 12, backgroundColor: '#f8f8f8', borderRadius: 4 },
  billToLabel: { fontSize: 8, color: '#999', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 },
  billToName: { fontSize: 13, fontWeight: 'bold', marginBottom: 2 },
  billToDetail: { fontSize: 9, color: '#666', marginTop: 1 },
  table: { marginBottom: 20 },
  tableHeader: { flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: RED, paddingBottom: 6, marginBottom: 4 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#ddd', paddingVertical: 6 },
  colDesc: { flex: 3 },
  colDate: { width: 55, textAlign: 'center' },
  colQty: { width: 50, textAlign: 'right' },
  colUnit: { width: 60, textAlign: 'center' },
  colPrice: { width: 70, textAlign: 'right' },
  colAmt: { width: 80, textAlign: 'right' },
  thText: { fontSize: 8, fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: 1 },
  tdText: { fontSize: 10 },
  totals: { alignItems: 'flex-end', marginTop: 10 },
  totalRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 4 },
  totalLabel: { width: 100, textAlign: 'right', fontSize: 10, color: '#666', marginRight: 12 },
  totalVal: { width: 80, textAlign: 'right', fontSize: 10 },
  grandTotal: { flexDirection: 'row', justifyContent: 'flex-end', borderTopWidth: 2, borderTopColor: RED, paddingTop: 6, marginTop: 4 },
  grandLabel: { width: 100, textAlign: 'right', fontSize: 13, fontWeight: 'bold', color: RED, marginRight: 12 },
  grandVal: { width: 80, textAlign: 'right', fontSize: 13, fontWeight: 'bold', color: RED },
  footer: { position: 'absolute', bottom: 40, left: 40, right: 40, borderTopWidth: 0.5, borderTopColor: '#ccc', paddingTop: 8 },
  footerText: { fontSize: 8, color: '#999', textAlign: 'center' },
  divider: { borderBottomWidth: 1, borderBottomColor: RED, marginBottom: 20, opacity: 0.3 },
});

const fmt = v => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => { if (!d) return ''; const [y, m, day] = d.split('-'); const dt = new Date(+y, +m - 1, +day); return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

export default function InvoicePDF({ sale }) {
  const { biz, customer, invoiceNumber, saleDate, items, subtotal, tax, total, notes } = sale;
  const hasItemDates = items.some(item => item.item_date);

  return (
    <Document>
      <Page size="LETTER" style={s.page}>
        {/* Header */}
        <View style={s.header}>
          <View style={s.logoBlock}>
            {biz.logoUrl ? <Image src={biz.logoUrl} style={s.logo} /> : null}
            {!biz.logoUrl && <Text style={s.bizName}>{biz.name}</Text>}
            {biz.address ? <Text style={s.bizDetail}>{biz.address}</Text> : null}
            {biz.email ? <Text style={s.bizDetail}>{biz.email}</Text> : null}
          </View>
          <View style={s.invoiceBlock}>
            <Text style={s.invoiceTitle}>INVOICE</Text>
            <Text style={s.invoiceNum}>#{invoiceNumber}</Text>
            <Text style={s.invoiceDate}>{saleDate}</Text>
          </View>
        </View>

        <View style={s.divider} />

        {/* Bill To */}
        <View style={s.billTo}>
          <Text style={s.billToLabel}>Bill To</Text>
          <Text style={s.billToName}>{titleCase(customer.name)}</Text>
          {customer.contactName ? <Text style={s.billToDetail}>{titleCase(customer.contactName)}</Text> : null}
          {customer.billingStreet ? <Text style={s.billToDetail}>{titleCase(customer.billingStreet)}</Text> : null}
          {(customer.billingCity || customer.billingState || customer.billingZip) ? <Text style={s.billToDetail}>{[titleCase(customer.billingCity), [(customer.billingState||'').toUpperCase(), customer.billingZip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</Text> : null}
          {customer.contactEmail ? <Text style={s.billToDetail}>{customer.contactEmail}</Text> : null}
        </View>

        {/* Line Items Table */}
        <View style={s.table}>
          <View style={s.tableHeader}>
            <Text style={[s.thText, s.colDesc]}>Description</Text>
            {hasItemDates && <Text style={[s.thText, s.colDate]}>Date</Text>}
            <Text style={[s.thText, s.colQty]}>Qty</Text>
            <Text style={[s.thText, s.colUnit]}>Unit</Text>
            <Text style={[s.thText, s.colPrice]}>Unit Price</Text>
            <Text style={[s.thText, s.colAmt]}>Amount</Text>
          </View>
          {items.map((item, i) => (
            <View key={i} style={s.tableRow}>
              <Text style={[s.tdText, s.colDesc]}>{item.description}</Text>
              {hasItemDates && <Text style={[s.tdText, s.colDate, { fontSize: 9, color: '#666' }]}>{fmtDate(item.item_date)}</Text>}
              <Text style={[s.tdText, s.colQty]}>{item.qty}</Text>
              <Text style={[s.tdText, s.colUnit]}>{item.unit || ''}</Text>
              <Text style={[s.tdText, s.colPrice]}>${fmt(item.unitPrice)}</Text>
              <Text style={[s.tdText, s.colAmt]}>${fmt(item.amount)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={s.totals}>
          <View style={s.totalRow}>
            <Text style={s.totalLabel}>Subtotal</Text>
            <Text style={s.totalVal}>${fmt(subtotal)}</Text>
          </View>
          {Number(tax) > 0 && (
            <View style={s.totalRow}>
              <Text style={s.totalLabel}>Tax</Text>
              <Text style={s.totalVal}>${fmt(tax)}</Text>
            </View>
          )}
          <View style={s.grandTotal}>
            <Text style={s.grandLabel}>Total</Text>
            <Text style={s.grandVal}>${fmt(total)}</Text>
          </View>
        </View>

        {/* Notes */}
        {notes ? (
          <View style={{ marginTop: 20 }}>
            <Text style={{ fontSize: 8, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Notes</Text>
            <Text style={{ fontSize: 9, color: '#666' }}>{notes}</Text>
          </View>
        ) : null}

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerText}>Payment terms: Net 30</Text>
          {biz.name && <Text style={s.footerText}>{biz.name} — Thank you for your business</Text>}
        </View>
      </Page>
    </Document>
  );
}
